import React, { useState, useEffect } from "react";
import {
  Table,
  InputGroup,
  FormControl,
  Dropdown,
  Button,
} from "react-bootstrap";
import CallDetails from "./call_details/callDetails";
import authService from "../../../services/authService";
import { useUser } from "../../../context/UserContext";

const CallLogs = () => {
  const { token } = useUser();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("Select");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCall, setSelectedCall] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localTimezone, setLocalTimezone] = useState("");
  const recordsPerPage = 7;

  useEffect(() => {
    const fetchCallLogs = async () => {
      try {
        let data;
        if (filter === "This Month") {
          data = await authService.getCallLogsThisMonth(token);
        } else if (filter === "This Quarter") {
          data = await authService.getCallLogsThisQuarter(token);
        } else {
          data = await authService.getCallLogs(token);
        }

        setCallLogs(data);
      } catch (error) {
        console.error("Failed to fetch call logs data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCallLogs();
  }, [token, filter]);

  useEffect(() => {
    try {
      const formatter = new Intl.DateTimeFormat(undefined, {
        timeZoneName: "short",
      });
      // The format() method needs a date object, but we only care about the timezone name.
      // Using a dummy date won't affect the timezone name.
      const parts = formatter.formatToParts(new Date());
      const timeZonePart = parts.find((part) => part.type === "timeZoneName");
      if (timeZonePart) {
        setLocalTimezone(timeZonePart.value);
      } else {
        setLocalTimezone("Local Time"); // Fallback if timezone name can't be determined
      }
    } catch (error) {
      console.error("Error getting local timezone:", error);
      setLocalTimezone("Local Time"); // Fallback in case of error
    }
  }, []);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = callLogs.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(callLogs.length / recordsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleRowClick = (call) => {
    setSelectedCall(call);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedCall(null);
  };

  // Function to convert call logs to CSV format
  const convertToCSV = (data) => {
    // Define CSV headers based on the data structure
    const headers = [
      "Caller Name",
      "Phone",
      "Location",
      "Date & Time",
      "Status",
      "Transcription Summary",
    ];

    // Create CSV header row
    let csvContent = headers.join(",") + "\n";

    // Add data rows
    data.forEach((log) => {
      const formattedDate = log.startTime
        ? new Date(log.startTime).toLocaleString()
        : "NA";
      const name = log.name || "NA";
      const phone = log.fromPhoneNumber || "NA";
      const location = log.location || "NA";
      const status = log.status || "NA";
      const summary = log.transcriptionSummary || "NA";

      // Handle potential commas in text fields by wrapping in quotes
      const row = [
        `"${name}"`,
        `"${phone}"`,
        `"${location}"`,
        `"${formattedDate}"`,
        `"${status}"`,
        `"${summary.replace(/"/g, '""')}"`, // Escape quotes in the summary
      ];

      csvContent += row.join(",") + "\n";
    });

    return csvContent;
  };

  // Function to trigger CSV download
  const handleDownloadCSV = () => {
    const csvData = convertToCSV(callLogs);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    // Create a link element and trigger download
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().slice(0, 10);
    link.setAttribute("href", url);
    link.setAttribute("download", `call_logs_${timestamp}.csv`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div className="mx-auto">Loading...</div>;
  }

  return (
    <div className="card  border-0 py-4 px-0 rounded-4 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 px-4">
        <h4 className="fs-three">Call Logs</h4>
        <div className="d-flex gap-3">
          <InputGroup className="border border-1 rounded search-input-group">
            <InputGroup.Text className="search-icon bg-white">
              <img src="/assets/search.svg" alt="Search" />
            </InputGroup.Text>
            <FormControl
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input border-start-0 ps-0"
            />
          </InputGroup>
          <Dropdown>
            <Dropdown.Toggle
              variant="outline-primary"
              className="border-purple-color fw-bold"
            >
              {filter}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setFilter("Select")}>
                Select
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setFilter("This Month")}>
                This Month
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setFilter("This Quarter")}>
                This Quarter
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button variant="primary" onClick={handleDownloadCSV}>
            <img
              src="/assets/download.svg"
              alt="Icon"
              className="img-fluid me-2"
            />
            Download CSV
          </Button>
        </div>
      </div>

      <Table hover className="my-3">
        <thead>
          <tr className="fs-one border-top">
            <th>Caller Name</th>
            <th>Phone</th>
            <th>
              Location{" "}
              <img
                src="/assets/arrow-down-gray.svg"
                alt="Icon"
                className="img-fluid"
              />
            </th>
            <th>Date & Time ({localTimezone || "Local Time"})</th>
            <th>
              Status{" "}
              <img
                src="/assets/arrow-down-gray.svg"
                alt="Icon"
                className="img-fluid"
              />
            </th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentRecords.map((log, index) => (
            <tr
              key={index}
              className="table-row fs-one"
              onClick={() => handleRowClick(log)}
              style={{ cursor: "pointer" }}
            >
              <td>
                <strong>{log.name || "NA"}</strong>
              </td>
              <td>{log.fromPhoneNumber || "NA"}</td>
              <td>{log.location || "NA"}</td>
              <td>
                {log.startTime
                  ? new Date(log.startTime).toLocaleString()
                  : "NA"}
              </td>
              <td>{log.status || "NA"}</td>
              <td>
                <img
                  src="/assets/arrow-right.svg"
                  alt="Icon"
                  className="img-fluid action-icon"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center px-4">
        <Button
          variant={currentPage === 1 ? "light" : "primary"}
          className={
            currentPage === 1
              ? "border-dark-subtle"
              : "border-purple-color fw-bold"
          }
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </Button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <Button
          variant={currentPage === totalPages ? "light" : "primary"}
          className={
            currentPage === totalPages
              ? "border-dark-subtle"
              : "border-purple-color fw-bold"
          }
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
      </div>

      <CallDetails
        show={showDetailsModal}
        handleClose={handleCloseDetailsModal}
        call={selectedCall}
      />
    </div>
  );
};

export default CallLogs;
